import React, { useState, useEffect } from 'react';
import { performanceReviewAPI, teamMembersAPI } from '../utils/api';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { format } from 'date-fns';
import {
  ClipboardCheck,
  Star,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Calendar,
  User,
  Search,
  Filter,
  FileText,
  TrendingUp,
  TrendingDown,
  Clock,
  Users,
} from 'lucide-react';

const PerformanceReviews = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('pending');
  const [pendingAssignments, setPendingAssignments] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterPoor, setFilterPoor] = useState(false);
  
  const [reviewForm, setReviewForm] = useState({
    job_status: 'complete',
    incomplete_reason: '',
    workmanship: 7,
    neatness: 7,
    wastefulness: 7,
    timeliness: 7,
    attitude_to_workers: 7,
    attitude_to_client: 7,
    notes: '',
  });

  const isManager = user?.role === 'admin' || user?.role === 'manager';

  useEffect(() => {
    loadData();
  }, [activeTab, filterPoor]);

  const loadData = async () => {
    try {
      setLoading(true);
      if (activeTab === 'pending') {
        const res = await performanceReviewAPI.getPendingAssignments();
        setPendingAssignments(res.data);
      } else {
        const params = filterPoor ? { is_poor: true } : {};
        const res = await performanceReviewAPI.getAll(params);
        setReviews(res.data);
      }
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const openReviewModal = (assignment) => {
    setSelectedAssignment(assignment);
    setReviewForm({
      job_status: 'complete',
      incomplete_reason: '',
      workmanship: 7,
      neatness: 7,
      wastefulness: 7,
      timeliness: 7,
      attitude_to_workers: 7,
      attitude_to_client: 7,
      notes: '',
    });
    setShowReviewModal(true);
  };

  const handleSubmitReview = async (e) => {
    e.preventDefault();
    try {
      await performanceReviewAPI.create({
        assignment_id: selectedAssignment.id,
        ...reviewForm,
      });
      toast.success('Review submitted successfully');
      setShowReviewModal(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit review');
    }
  };

  const calculateScore = () => {
    if (reviewForm.job_status === 'incomplete') return null;
    const ratings = [
      reviewForm.workmanship,
      reviewForm.neatness,
      reviewForm.wastefulness,
      reviewForm.timeliness,
      reviewForm.attitude_to_workers,
      reviewForm.attitude_to_client,
    ];
    return (ratings.reduce((a, b) => a + b, 0) / ratings.length).toFixed(1);
  };

  const getScoreColor = (score) => {
    if (score >= 8) return 'text-green-600 bg-green-100';
    if (score >= 6) return 'text-amber-600 bg-amber-100';
    return 'text-red-600 bg-red-100';
  };

  const filteredPending = pendingAssignments.filter(a =>
    a.member?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    a.job?.job_code?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredReviews = reviews.filter(r =>
    r.member_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    r.job_code?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
          <ClipboardCheck className="w-6 h-6 text-blue-600" />
          Performance Reviews
        </h1>
        <p className="text-slate-600">Review team member performance on job assignments</p>
      </div>

      {/* Tabs */}
      <div className="border-b border-slate-200 mb-6">
        <nav className="flex gap-4">
          <button
            onClick={() => setActiveTab('pending')}
            className={`flex items-center gap-2 px-4 py-3 border-b-2 transition-colors ${
              activeTab === 'pending' ? 'border-blue-600 text-blue-600' : 'border-transparent text-slate-600'
            }`}
          >
            <Clock className="w-4 h-4" />
            Pending Reviews ({pendingAssignments.length})
          </button>
          <button
            onClick={() => setActiveTab('completed')}
            className={`flex items-center gap-2 px-4 py-3 border-b-2 transition-colors ${
              activeTab === 'completed' ? 'border-blue-600 text-blue-600' : 'border-transparent text-slate-600'
            }`}
          >
            <CheckCircle2 className="w-4 h-4" />
            Completed Reviews ({reviews.length})
          </button>
        </nav>
      </div>

      {/* Filters */}
      <div className="bg-white border border-slate-200 rounded-xl p-4 mb-6">
        <div className="flex flex-col md:flex-row md:items-center gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by member or job..."
              className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg text-sm"
            />
          </div>
          {activeTab === 'completed' && (
            <label className="flex items-center gap-2 text-sm text-slate-600">
              <input
                type="checkbox"
                checked={filterPoor}
                onChange={(e) => setFilterPoor(e.target.checked)}
                className="rounded border-slate-300"
              />
              Show only poor performance
            </label>
          )}
        </div>
      </div>

      {/* Pending Reviews Tab */}
      {activeTab === 'pending' && (
        <div className="space-y-4">
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            </div>
          ) : filteredPending.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <CheckCircle2 className="w-12 h-12 mx-auto mb-2 text-slate-300" />
              <p>No pending reviews</p>
            </div>
          ) : (
            filteredPending.map((assignment) => (
              <div key={assignment.id} className="bg-white border border-slate-200 rounded-xl p-6">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-3 mb-2">
                      <span className="text-xs font-mono bg-slate-100 text-slate-600 px-2 py-1 rounded">
                        {assignment.job?.job_code}
                      </span>
                      <h3 className="font-semibold text-slate-900">{assignment.job?.title}</h3>
                    </div>
                    <div className="flex items-center gap-4 text-sm text-slate-600">
                      <div className="flex items-center gap-1">
                        <User className="w-4 h-4" />
                        <span>{assignment.member?.employee_id ? `${assignment.member.employee_id} – ` : ''}{assignment.member?.name}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        <span>
                          {format(new Date(assignment.assignment_start), 'MMM dd')} - {format(new Date(assignment.assignment_end), 'MMM dd, yyyy')}
                        </span>
                      </div>
                    </div>
                  </div>
                  {isManager && (
                    <button
                      onClick={() => openReviewModal(assignment)}
                      className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-lg flex items-center gap-2"
                    >
                      <Star className="w-4 h-4" />
                      Review
                    </button>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Completed Reviews Tab */}
      {activeTab === 'completed' && (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            </div>
          ) : filteredReviews.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <FileText className="w-12 h-12 mx-auto mb-2 text-slate-300" />
              <p>No reviews found</p>
            </div>
          ) : (
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left px-4 py-3 text-sm font-medium text-slate-700">Employee</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-slate-700">Job</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-slate-700">Status</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-slate-700">Score</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-slate-700">Date</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-slate-700">Reviewer</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {filteredReviews.map((review) => (
                  <tr key={review.id} className="hover:bg-slate-50">
                    <td className="px-4 py-3">
                      <span className="font-medium text-slate-900">{review.member_name}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-xs font-mono bg-slate-100 text-slate-600 px-2 py-0.5 rounded mr-2">
                        {review.job_code}
                      </span>
                      <span className="text-slate-600 text-sm">{review.job_title}</span>
                    </td>
                    <td className="px-4 py-3">
                      {review.job_status === 'complete' ? (
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded text-xs">
                          <CheckCircle2 className="w-3 h-3" />
                          Complete
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-red-100 text-red-700 rounded text-xs">
                          <XCircle className="w-3 h-3" />
                          Incomplete
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {review.overall_score ? (
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-sm font-medium ${getScoreColor(review.overall_score)}`}>
                          {review.overall_score >= 6 ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
                          {review.overall_score}/10
                        </span>
                      ) : (
                        <span className="text-slate-400">N/A</span>
                      )}
                      {review.is_poor_performance && (
                        <AlertTriangle className="w-4 h-4 text-red-500 inline ml-2" title="Poor performance" />
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-600">
                      {format(new Date(review.review_date), 'MMM dd, yyyy')}
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-600">
                      {review.reviewer_name}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Review Modal */}
      {showReviewModal && selectedAssignment && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-slate-200">
              <h3 className="text-lg font-semibold text-slate-900">Submit Performance Review</h3>
              <p className="text-sm text-slate-600 mt-1">
                {selectedAssignment.member?.name} - {selectedAssignment.job?.job_code} {selectedAssignment.job?.title}
              </p>
            </div>

            <form onSubmit={handleSubmitReview} className="p-6 space-y-6">
              {/* Job Completion Status */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Job Completion Status *</label>
                <div className="flex gap-4">
                  <label className={`flex-1 p-4 border-2 rounded-lg cursor-pointer transition-colors ${
                    reviewForm.job_status === 'complete' ? 'border-green-500 bg-green-50' : 'border-slate-200'
                  }`}>
                    <input
                      type="radio"
                      name="job_status"
                      value="complete"
                      checked={reviewForm.job_status === 'complete'}
                      onChange={(e) => setReviewForm({ ...reviewForm, job_status: e.target.value })}
                      className="sr-only"
                    />
                    <div className="flex items-center gap-2">
                      <CheckCircle2 className={`w-5 h-5 ${reviewForm.job_status === 'complete' ? 'text-green-600' : 'text-slate-400'}`} />
                      <span className="font-medium">Complete</span>
                    </div>
                  </label>
                  <label className={`flex-1 p-4 border-2 rounded-lg cursor-pointer transition-colors ${
                    reviewForm.job_status === 'incomplete' ? 'border-red-500 bg-red-50' : 'border-slate-200'
                  }`}>
                    <input
                      type="radio"
                      name="job_status"
                      value="incomplete"
                      checked={reviewForm.job_status === 'incomplete'}
                      onChange={(e) => setReviewForm({ ...reviewForm, job_status: e.target.value })}
                      className="sr-only"
                    />
                    <div className="flex items-center gap-2">
                      <XCircle className={`w-5 h-5 ${reviewForm.job_status === 'incomplete' ? 'text-red-600' : 'text-slate-400'}`} />
                      <span className="font-medium">Incomplete</span>
                    </div>
                  </label>
                </div>
              </div>

              {/* Incomplete Reason */}
              {reviewForm.job_status === 'incomplete' && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Reason for Incompletion *</label>
                  <textarea
                    value={reviewForm.incomplete_reason}
                    onChange={(e) => setReviewForm({ ...reviewForm, incomplete_reason: e.target.value })}
                    rows={3}
                    required
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg"
                    placeholder="Explain why the job was not completed..."
                  />
                </div>
              )}

              {/* Performance Ratings */}
              {reviewForm.job_status === 'complete' && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-4">Performance Ratings (1-10)</label>
                  <div className="grid grid-cols-2 gap-4">
                    {[
                      { key: 'workmanship', label: 'Workmanship', desc: 'Quality of work' },
                      { key: 'neatness', label: 'Neatness', desc: 'Cleanliness & organization' },
                      { key: 'wastefulness', label: 'Resource Efficiency', desc: '10 = No waste' },
                      { key: 'timeliness', label: 'Timeliness', desc: 'On-time completion' },
                      { key: 'attitude_to_workers', label: 'Team Attitude', desc: 'Collaboration with team' },
                      { key: 'attitude_to_client', label: 'Client Attitude', desc: 'Client interaction' },
                    ].map((item) => (
                      <div key={item.key} className="bg-slate-50 p-3 rounded-lg">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm font-medium text-slate-700">{item.label}</span>
                          <span className={`text-lg font-bold ${
                            reviewForm[item.key] >= 8 ? 'text-green-600' :
                            reviewForm[item.key] >= 6 ? 'text-amber-600' : 'text-red-600'
                          }`}>
                            {reviewForm[item.key]}
                          </span>
                        </div>
                        <p className="text-xs text-slate-500 mb-2">{item.desc}</p>
                        <input
                          type="range"
                          min="1"
                          max="10"
                          value={reviewForm[item.key]}
                          onChange={(e) => setReviewForm({ ...reviewForm, [item.key]: parseInt(e.target.value) })}
                          className="w-full accent-blue-600"
                        />
                      </div>
                    ))}
                  </div>

                  {/* Score Preview */}
                  <div className="mt-4 p-4 bg-slate-100 rounded-lg flex items-center justify-between">
                    <span className="text-slate-700 font-medium">Overall Score:</span>
                    <span className={`text-2xl font-bold ${getScoreColor(parseFloat(calculateScore()))}`}>
                      {calculateScore()}/10
                    </span>
                  </div>
                  {parseFloat(calculateScore()) < 6 && (
                    <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
                      <AlertTriangle className="w-5 h-5" />
                      <span className="text-sm">This score will trigger a poor performance alert</span>
                    </div>
                  )}
                </div>
              )}

              {/* Notes Field */}
              <div className="mt-4">
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  <FileText className="w-4 h-4 inline mr-1" />
                  Review Notes
                </label>
                <textarea
                  value={reviewForm.notes}
                  onChange={(e) => setReviewForm({ ...reviewForm, notes: e.target.value })}
                  placeholder="Add any additional notes about this review..."
                  rows={3}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-4 border-t border-slate-200">
                <button
                  type="button"
                  onClick={() => setShowReviewModal(false)}
                  className="px-4 py-2 text-slate-700 hover:bg-slate-100 rounded-lg"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg flex items-center gap-2"
                >
                  <ClipboardCheck className="w-4 h-4" />
                  Submit Review
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default PerformanceReviews;
